import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  "relative w-full rounded-md border p-4 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/50 bg-destructive/10 text-destructive [&_svg]:text-destructive",
        success: "border-success/50 bg-success/10 text-success [&_svg]:text-success",
        warning: "border-warning/50 bg-warning/10 text-warning [&_svg]:text-warning",
        info: "border-info/50 bg-info/10 text-info [&_svg]:text-info"
      }
    },
    defaultVariants: { variant: "default" }
  }
);

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  icon?: React.ReactNode;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(({ className, variant, icon, children, ...props }, ref) => {
  const role = variant === "destructive" ? "alert" : "status";
  return (
  <div ref={ref} role={role} className={cn(alertVariants({ variant }), className)} {...props}>
    <div className="flex gap-3">
      {icon ? <span className="mt-0.5 shrink-0" aria-hidden="true">{icon}</span> : null}
      <div className="flex-1">{children}</div>
    </div>
  </div>
  );
});
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5 ref={ref} className={cn("mb-1 text-sm font-medium leading-none", className)} {...props} />
  )
);
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-current/80", className)} {...props} />
  )
);
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription, alertVariants };
